import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuthStore } from '../../store/authStore';
import { dashboardApi, TeacherDashboardData } from '../../services/dashboardApi';
import { CourseGroups } from '../../components/teacherAnalytics/CourseGroups';
import { Panel, Records, Surface } from '../../components/teacherAnalytics/AnalyticsShared';

/** Existing risk feature stays separate; none of these legacy metrics feed analytics. */
function ExistingRisk() {
  const token = useAuthStore(s => s.token);
  const [state, setState] = useState<{token: string | null; data?: TeacherDashboardData; error?: boolean}>({token});
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    setState({token});
    dashboardApi.getTeacher(controller.signal).then(data => {if (current) setState({token, data});})
      .catch(() => {if (current) setState({token, error: true});});
    return () => {current = false; controller.abort();};
  }, [token, revision]);
  const data = state.token === token ? state.data : undefined;
  return <Panel><h2>需关注的学生 · 原有预警功能</h2>
    <p>此处为独立的原有预警，不作为课程学习记录概览。</p>
    {!data && (state.error ? <div role="alert">无法加载学生预警数据 <button onClick={() => setRevision(r => r + 1)}>重试</button></div> :
      <p role="status">正在加载预警数据…</p>)}
    {data && <>
      <p>预警标准：连续 {data.criteria.inactiveDays} 天未活动 · 进度落后班级中位数 {data.criteria.progressGapPoints} 个百分点 ·
        练习错误率超过 {data.criteria.exerciseFailureRatePercent}%（至少 {data.criteria.minAttemptsForFailureRate} 次作答）</p>
      {!data.atRisk.length && <p>目前没有触发预警的学生</p>}
      <Records>{data.atRisk.map(s => <article key={`${s.courseId}/${s.studentId}`}><h3>{s.studentName || '未命名学生'} · {s.riskLevel === 'high' ? '需重点关注' : '需留意'}</h3>
        <p>进度 {s.progress}% · 中位数 {s.cohortMedian}% · 未活动 {s.inactiveDays === null ? '—' : `${s.inactiveDays} 天`} ·
          错误率 {s.exerciseFailureRate === null ? '—' : `${s.exerciseFailureRate}%`}</p>
        <p>{s.signals.map(v => (({inactive: '长期未活动', behind_cohort: '进度落后', failing_exercises: '练习正确率低'} as Record<string, string>)[v] || v)).join(' · ')}</p>
        <Link to={`/teacher/courses/${s.courseId}/students/${s.studentId}`}>查看学习详情</Link>
      </article>)}</Records>
    </>}
  </Panel>;
}
export const TeacherDashboard: React.FC = () => {
  const user = useAuthStore(s => s.user);
  const [params, setParams] = useSearchParams();
  const view = params.get('view') || 'overview';
  const status = user?.teacherInfo?.approvalStatus || 'pending';
  const approved = status === 'approved';
  return <Surface><DashboardLayout compact title={approved ? '欢迎回来，教师！' : status === 'rejected' ? '申请已被拒绝' : '申请审核中'}
    subtitle="管理您的课程并查看学生学习记录">
    <Panel><h2>账号状态：{approved ? '已批准' : status === 'rejected' ? '已拒绝' : '审核中'}</h2>
      {!approved && <p>{status === 'rejected' ? `您的教师申请已被拒绝。原因：${user?.teacherInfo?.rejectionReason || '未说明'}` :
        '您的教师账号正在等待管理员审核批准，请耐心等待。'}</p>}
    </Panel>
    {approved && <>
      <nav aria-label="教师工作台">{[
        ['overview', '概览'], ['atRisk', '需关注学生'], ['courses', '课程列表'], ['students', '学生列表']
      ].map(([key, label]) => <Link key={key} to={`?view=${key}&page=1`} aria-current={view === key ? 'page' : undefined}>{label}</Link>)}</nav>
      {view === 'atRisk' ? <ExistingRisk /> : <CourseGroups />}
      {!['overview', 'atRisk', 'courses', 'students'].includes(view) && <p role="alert">链接或查询条件无效。</p>}
      <nav aria-label="快捷操作"><Link to="/teacher/courses/new">创建课程</Link><Link to="/teacher/courses">管理课程</Link>
        <button onClick={() => setParams({view: 'students', page: '1'})}>按课程查看学生</button></nav>
    </>}
  </DashboardLayout></Surface>;
};
